import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch } from "../../utils/api";
import "../../styles/admin/admin-form.css";

const TIPO_AGENCIA_HARCODED = "Agente Aduanal";

const DIAS_SEMANA = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];

// Estructura inicial del horario vacía para evitar errores de undefined
const HORARIO_POR_DEFECTO = DIAS_SEMANA.reduce((acc, dia) => {
  acc[dia] = [];
  return acc;
}, {});

const initialState = {
  nombre: '',
  telefono: '',
  correoElectronico: '',
  direccion: '',
  estado: '',
  municipio: '',
  imagenUrl: '', 
  tipo: TIPO_AGENCIA_HARCODED,
  horario: HORARIO_POR_DEFECTO, 
};

const FormularioAgenteAduanal = () => {
  const [formData, setFormData] = useState(initialState);
  const [error, setError] = useState(null); 
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const [pageLoading, setPageLoading] = useState(false); 

  // Iniciamos con un turno vacío para que aparezca al menos una fila
  const [generalHorario, setGeneralHorario] = useState([{ abre: "", cierra: "" }]);
  // -------------------------------------------------------------
  
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id); 

  useEffect(() => {
    if (isEditMode) {
      setPageLoading(true); 
      setError(null);
      
      authFetch(`/api/Agencia/${id}`)
        .then(res => {
          if (!res.ok) {
            throw new Error('No se pudieron cargar los datos del agente.');
          }
          return res.json();
        })
        .then(data => {
          const horarioData = data.horario ? { ...HORARIO_POR_DEFECTO, ...data.horario } : HORARIO_POR_DEFECTO;
          
          setFormData({ 
            ...initialState, 
            ...data, 
            imagenUrl: data.urlImagen || data.UrlImagen || data.url_imagen || '', 
            horario: horarioData, 
            tipo: TIPO_AGENCIA_HARCODED 
          }); 
        })
        .catch(err => {
          setError(err.message);
        })
        .finally(() => {
          setPageLoading(false); 
        });
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // --- MANEJO DEL FORMULARIO PRINCIPAL ---
  const handleHorarioChange = (dia, index, campo, value) => {
    setFormData(prev => {
      const nuevoHorario = JSON.parse(JSON.stringify(prev.horario));
      if (nuevoHorario[dia] && nuevoHorario[dia][index]) {
        nuevoHorario[dia][index][campo] = value;
      }
      return { ...prev, horario: nuevoHorario };
    });
  };

  const addTurno = (dia) => { 
    setFormData(prev => {
      const nuevoHorario = { ...prev.horario };
      const nuevosSlots = [...(nuevoHorario[dia] || []), { abre: '09:00', cierra: '18:00' }]; 
      nuevoHorario[dia] = nuevosSlots;
      return { ...prev, horario: nuevoHorario };
    });
  };

  const removeTurno = (dia, index) => { 
    setFormData(prev => {
      const nuevoHorario = { ...prev.horario };
      const nuevosSlots = nuevoHorario[dia].filter((_, i) => i !== index);
      nuevoHorario[dia] = nuevosSlots;
      return { ...prev, horario: nuevoHorario };
    });
  };

  // --- FUNCIONES PARA EL RECUADRO GENERAL (Configuración Masiva) ---

  const handleGeneralChange = (index, campo, value) => {
    const nuevos = [...generalHorario];
    nuevos[index] = { ...nuevos[index], [campo]: value };
    setGeneralHorario(nuevos);
  };

  const addGeneralTurn = () => {
    setGeneralHorario([...generalHorario, { abre: "", cierra: "" }]);
  };

  const removeGeneralTurn = (index) => {
    setGeneralHorario(generalHorario.filter((_, i) => i !== index));
  };

  const aplicarHorarioGeneral = () => {
    // Validar que haya al menos un turno completo
    const turnosValidos = generalHorario.filter(t => t.abre && t.cierra);

    if (turnosValidos.length === 0) {
      alert("⚠️ Por favor define al menos un turno completo (Apertura y Cierre) en la configuración general.");
      return;
    }

    const confirmacion = window.confirm(
      `¿Estás seguro? Se aplicarán ${turnosValidos.length} turno(s) a todos los días de LUNES a VIERNES, sobrescribiendo lo actual.`
    );

    if (!confirmacion) return;

    setFormData(prev => {
      const nuevoHorario = { ...prev.horario };
      const diasLaborales = ["lunes", "martes", "miercoles", "jueves", "viernes"];
      
      diasLaborales.forEach(dia => {
        // Copiamos los turnos válidos del general al día específico
        // Usamos map para crear nuevos objetos y evitar referencias compartidas
        nuevoHorario[dia] = turnosValidos.map(t => ({ ...t }));
      });
      
      return { ...prev, horario: nuevoHorario };
    });

    alert("✅ Horarios (incluyendo turnos partidos) aplicados a Lunes-Viernes.");
  };
  // ---------------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); 
    setError(null); 
    
    const payload = { 
        ...formData, 
        tipo: TIPO_AGENCIA_HARCODED,
        UrlImagen: formData.imagenUrl 
    };
    
    delete payload.web; 

    const url = isEditMode 
      ? `/api/Agencia/${id}` 
      : `/api/Agencia`;
      
    const method = isEditMode ? 'PUT' : 'POST';

    try {
      const response = await authFetch(url, {
        method: method,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (jsonError) {
          errorData = { message: errorText || 'Error al guardar. Código: ' + response.status };
        }
        throw new Error(errorData.message || 'Error desconocido al guardar.');
      }

      alert(isEditMode ? 'Agente actualizado exitosamente' : 'Agente guardado exitosamente');
      navigate('/admin/agenteAduanal'); 

    } catch (err) {
      if (err.message.includes('Failed to fetch')) {
          setError('Error de conexión con la API.');
      } else {
          setError(err.message);
      }
      alert('Error al guardar: ' + (err.message || 'Error de conexión'));
    } finally {
      setIsSubmitting(false); 
    }
  };

  return (
    <div className="form-container">

      <button className="btn-back" onClick={() => navigate(-1)} disabled={isSubmitting}>
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/></svg>
        Volver
      </button>

      <h1>{isEditMode ? 'Editar Agente Aduanal' : 'Añadir Nuevo Agente Aduanal'}</h1>

      {error && <div className="form-error">Error: {error}</div>}

      {pageLoading && (
        <div className="form-loading">
          Cargando datos del agente...
        </div>
      )}

      {!pageLoading && (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="nombre">Nombre (*)</label>
            <input
              id="nombre"
              name="nombre"
              type="text"
              placeholder="Ej. Agencia Aduanal Pérez"
              value={formData.nombre}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="imagenUrl">URL de Imagen</label>
            <input
              id="imagenUrl"
              name="imagenUrl"
              type="url"
              placeholder="Ej. https://..."
              value={formData.imagenUrl}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            {formData.imagenUrl && (
                <div style={{ marginTop: '10px' }}>
                    <img 
                        src={formData.imagenUrl} 
                        alt="Previsualización" 
                        style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #ddd' }}
                        onError={(e) => e.target.style.display = 'none'} 
                    />
                </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="correoElectronico">Correo Electrónico</label>
            <input
              id="correoElectronico"
              name="correoElectronico"
              type="email"
              placeholder="Ej. contacto@aaperez.com"
              value={formData.correoElectronico}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="telefono">Teléfono</label>
            <input
              id="telefono"
              name="telefono"
              type="tel"
              placeholder="Ej. 921 123 4567"
              value={formData.telefono}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="municipio">Municipio</label>
              <input
                id="municipio"
                name="municipio"
                type="text"
                placeholder="Ej. Coatzacoalcos"
                value={formData.municipio}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="estado">Estado</label>
              <input
                id="estado"
                name="estado"
                type="text"
                placeholder="Ej. Veracruz"
                value={formData.estado}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="direccion">Dirección</label>
            <textarea
              id="direccion"
              name="direccion"
              placeholder="Ej. Calle H. Colegio Militar 200, Col. Centro"
              rows="3"
              value={formData.direccion}
              onChange={handleChange}
              disabled={isSubmitting}
            ></textarea>
          </div>

          <fieldset>
            <legend>Horarios de Atención</legend>

            {/* --- SECCIÓN CONFIGURACIÓN MASIVA CON TURNOS PARTIDOS --- */}
            <div style={{ 
              backgroundColor: '#e0f2fe', 
              padding: '15px', 
              borderRadius: '8px', 
              marginBottom: '25px', 
              border: '2px solid #0ea5e9' 
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, color: '#0369a1' }}>
                  ⚡ Configurar Horario General (Lunes a Viernes)
                </h4>
                <button 
                  type="button" 
                  onClick={addGeneralTurn}
                  style={{ 
                    fontSize: '0.8em', 
                    padding: '4px 8px', 
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    border: '1px solid #0369a1',
                    color: '#0369a1',
                    borderRadius: '4px'
                  }}
                >
                  + Agregar Turno Extra
                </button>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {generalHorario.map((turno, index) => (
                  <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '0.8em', fontWeight: 'bold' }}>Apertura</label>
                      <input 
                        type="time" 
                        value={turno.abre} 
                        onChange={(e) => handleGeneralChange(index, 'abre', e.target.value)} 
                        style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '0.8em', fontWeight: 'bold' }}>Cierre</label>
                      <input 
                        type="time" 
                        value={turno.cierra} 
                        onChange={(e) => handleGeneralChange(index, 'cierra', e.target.value)} 
                        style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </div>
                    {generalHorario.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeGeneralTurn(index)}
                        style={{ 
                          marginBottom: '2px', 
                          background: '#ef4444', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '4px', 
                          width: '30px', 
                          height: '30px', 
                          cursor: 'pointer' 
                        }}
                        title="Quitar este turno"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '15px' }}>
                <button 
                  type="button" 
                  onClick={aplicarHorarioGeneral}
                  style={{ 
                    backgroundColor: '#0284c7', 
                    color: 'white', 
                    border: 'none', 
                    padding: '10px 20px', 
                    borderRadius: '4px', 
                    cursor: 'pointer', 
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  Aplicar Horario a Días Laborales
                </button>
                <p style={{ fontSize: '0.85em', color: '#555', marginTop: '8px', fontStyle: 'italic' }}>
                  ℹ️ Define tus turnos (ej. mañana y tarde) aquí arriba y pulsa "Aplicar" para copiarlos automáticamente a todos los días entre semana.
                </p>
              </div>
            </div>
            {/* -------------------------------------------------------- */}
            
            {DIAS_SEMANA.map((dia) => (
              <div key={dia} className="horario-dia">
                <div className="horario-dia-header">
                  <h3>{dia.charAt(0).toUpperCase() + dia.slice(1)}</h3>
                  <button 
                    type="button" 
                    onClick={() => addTurno(dia)} 
                    className="btn-add-turno" 
                    disabled={isSubmitting}
                  >
                    + Añadir turno
                  </button>
                </div>

                {formData.horario[dia].length === 0 && (
                  <p className="horario-cerrado">Cerrado</p>
                )}

                {formData.horario[dia].map((turno, index) => (
                  <div key={index} className="horario-turno">
                    <div className="horario-inputs">
                      <div className="form-group">
                        <label htmlFor={`${dia}-abre-${index}`}>Abre</label>
                        <input
                          id={`${dia}-abre-${index}`}
                          type="time"
                          value={turno.abre}
                          onChange={(e) => handleHorarioChange(dia, index, 'abre', e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor={`${dia}-cierra-${index}`}>Cierra</label>
                        <input
                          id={`${dia}-cierra-${index}`}
                          type="time"
                          value={turno.cierra}
                          onChange={(e) => handleHorarioChange(dia, index, 'cierra', e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <button 
                      type="button" 
                      onClick={() => removeTurno(dia, index)} 
                      className="btn-remove-turno" 
                      title="Eliminar turno"
                      disabled={isSubmitting}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </fieldset>
          
          <button 
            type="submit" 
            className="btn-submit" 
            disabled={isSubmitting || pageLoading}
          >
            {isSubmitting ? 'Guardando...' : (isEditMode ? 'Guardar Cambios' : 'Guardar Agente Aduanal')}
          </button>
        </form>
      )} 
    </div>
  );
};

export default FormularioAgenteAduanal;